import React, { useState, useCallback, useRef } from 'react';
import {
  ProbabilityForecastWidgetInstanceConfig,
  ProbabilityForecastDataContent,
  forecastDirectionLabel,
} from './types';
import { WidgetEmptyState } from '../../../design';

interface ProbabilityForecastWidgetRendererProps {
  config: ProbabilityForecastWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: ProbabilityForecastDataContent) => void;
  readOnly?: boolean;
}

function colorFor(pct: number): string {
  if (pct >= 55) return 'text-success-500';
  if (pct <= 45) return 'text-danger-500';
  return 'text-neutral-400';
}

const ProbabilityForecastWidgetRenderer: React.FC<ProbabilityForecastWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const data = dashboardData[config.dataKey] as ProbabilityForecastDataContent | undefined;

  // No auto-default: an unanswered forecast reads as *unset* (null), not
  // silently pinned to 50% — that would look answered and defeat the gate.
  const toPct = (p?: number) => (typeof p === 'number' ? Math.round(p * 100) : null);
  const [pct, setPct] = useState<number | null>(toPct(data?.probUp));
  const lastProp = useRef(data?.probUp);

  if (data?.probUp !== lastProp.current) {
    lastProp.current = data?.probUp;
    setPct(toPct(data?.probUp));
  }

  const commit = useCallback(
    (nextPct: number) => {
      if (readOnly || !onDataChange) return;
      setPct(nextPct);
      onDataChange(config.dataKey, { ...(data ?? {}), probUp: nextPct / 100 });
    },
    [readOnly, onDataChange, config.dataKey, data],
  );

  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  const display = pct ?? 50;
  const isSet = pct !== null;

  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 px-4">
      <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">
        P(up after print)
      </span>
      <div className="text-center">
        <span className={`text-4xl font-bold tabular-nums ${isSet ? colorFor(display) : 'text-neutral-600'}`}>
          {isSet ? `${display}%` : '—'}
        </span>
        {isSet && (
          <p className={`text-xs font-medium mt-0.5 ${colorFor(display)}`}>
            {forecastDirectionLabel(display / 100)}
          </p>
        )}
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={display}
        disabled={readOnly}
        onChange={(e) => commit(Number(e.target.value))}
        aria-label="Probability the stock rises after the print"
        className="w-full max-w-[220px] accent-primary-500 disabled:opacity-60"
      />
      <div className="flex justify-between w-full max-w-[220px] text-[10px] text-neutral-500">
        <span>Down</span>
        <span>Up</span>
      </div>
      {!isSet && !readOnly && (
        <p className="text-xs text-danger-500 font-medium">Set a probability</p>
      )}
    </div>
  );
};

export default React.memo(ProbabilityForecastWidgetRenderer);
